'use client';

/**
 * What waits in the search bar to be sent: one pill per photo, link or note,
 * in a single row next to the "+" button. The row scrolls sideways (never
 * wraps: the card is height-clamped by --hero-slot-max) and fades at the
 * right edge while more chips sit past it.
 *
 * Link chips show the page's own photo and title as soon as /api/link-preview
 * answers (lib/link-preview); never a price: an Amazon price may only be
 * shown with the time Amazon gave it, which the card in the conversation does.
 *
 * Every control keeps the textarea's focus (onMouseDown preventDefault): on
 * phones the footer only exists while the bar has focus.
 */

import React, { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { MdClose } from 'react-icons/md';
import { HiDocumentText as NoteIcon, HiLink as LinkIcon } from 'react-icons/hi';
import type { LocaleDetails } from 'shared/src/types/languages.type';
import { getSearchUiContent } from '@/lib/ui-content/search-ui-content';
import { useLinkPreview } from '@/lib/link-preview/use-link-preview';
import type { LinkPreviewStatus } from '@/lib/link-preview/link-preview-cache';
import type { LinkPreview } from 'shared/src/types/link-preview.type';
import AmazonIcon from '@/public/icons/amazon-icon.svg';
import { noteChipLabel, type ComposerAttachment } from './composer-attachments';

type LinkItem = Extract<ComposerAttachment, { kind: 'link' }>;
type NoteItem = Extract<ComposerAttachment, { kind: 'note' }>;
type ImageItem = Extract<ComposerAttachment, { kind: 'image' }>;

const CHIP_CLASS =
  'flex max-w-[10rem] flex-shrink-0 items-center gap-1.5 rounded-full border border-zinc-200/80 bg-white/90 py-1 pl-1 pr-1.5 shadow-sm backdrop-blur-sm dark:border-white/10 dark:bg-white/10 sm:max-w-[13rem]';
const LEAD_CLASS =
  'flex h-6 w-6 flex-shrink-0 items-center justify-center overflow-hidden rounded-full bg-zinc-100 text-zinc-500 dark:bg-white/10 dark:text-zinc-300';

export function AttachmentChipShell({
  lead,
  label,
  title,
  removeLabel,
  onRemove,
  busy = false,
}: {
  lead: React.ReactNode;
  label: string;
  title?: string;
  removeLabel: string;
  onRemove?: () => void;
  busy?: boolean;
}) {
  return (
    <div className={CHIP_CLASS} title={title ?? label} aria-busy={busy || undefined}>
      <div className={`${LEAD_CLASS} ${busy ? 'animate-pulse' : ''}`}>{lead}</div>
      <span className="min-w-0 truncate text-xs text-zinc-600 dark:text-zinc-300">{label}</span>
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          onMouseDown={(e) => e.preventDefault()}
          aria-label={removeLabel}
          title={removeLabel}
          className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full bg-zinc-400/70 text-white transition-colors hover:bg-red-500 dark:bg-white/25 dark:hover:bg-red-500"
        >
          <MdClose className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export function ImageAttachmentChip({ item, locale, onRemove }: { item: ImageItem; locale: LocaleDetails; onRemove?: () => void }) {
  const searchContent = getSearchUiContent(locale);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  useEffect(() => {
    if (typeof URL.createObjectURL !== 'function') return;
    const url = URL.createObjectURL(item.file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [item.file]);
  return (
    <AttachmentChipShell
      lead={
        objectUrl ? (
          <Image
            src={objectUrl}
            alt={searchContent.selectedImage}
            width={24}
            height={24}
            className="h-full w-full object-cover"
            unoptimized // blob URL
          />
        ) : null
      }
      label={item.file.name}
      removeLabel={searchContent.removeImage}
      onRemove={onRemove}
    />
  );
}

export function LinkAttachmentChip({ item, locale, onRemove }: { item: LinkItem; locale: LocaleDetails; onRemove?: () => void }) {
  const { status, preview } = useLinkPreview(item.url);
  return <LinkAttachmentChipView item={item} locale={locale} onRemove={onRemove} status={status} preview={preview} />;
}

export function LinkAttachmentChipView({
  item,
  locale,
  onRemove,
  status,
  preview,
}: {
  item: LinkItem;
  locale: LocaleDetails;
  onRemove?: () => void;
  status: LinkPreviewStatus;
  preview: LinkPreview | null;
}) {
  const searchContent = getSearchUiContent(locale);
  const loading = status === 'loading' || status === 'idle';
  const title = preview && preview.kind !== 'unavailable' ? preview.title : null;
  const image = preview && preview.kind !== 'unavailable' ? preview.image : null;
  const unreadable = status === 'unavailable' || status === 'forbidden' || preview?.kind === 'unavailable';
  const label = title ?? item.host;
  const hint = loading ? searchContent.linkLoading : unreadable ? searchContent.linkUnavailable : title ?? item.host;
  return (
    <AttachmentChipShell
      busy={loading}
      lead={
        image ? (
          // eslint-disable-next-line @next/next/no-img-element
          <img src={image} alt="" className="h-full w-full bg-white object-contain" referrerPolicy="no-referrer" />
        ) : item.amazon ? (
          <AmazonIcon className="h-3 w-auto fill-current" aria-hidden="true" />
        ) : (
          <LinkIcon className="h-3.5 w-3.5" aria-hidden="true" />
        )
      }
      label={label}
      title={`${hint}\n${item.url}`}
      removeLabel={searchContent.removeLink}
      onRemove={onRemove}
    />
  );
}

export function NoteAttachmentChip({ item, locale, onRemove }: { item: NoteItem; locale: LocaleDetails; onRemove?: () => void }) {
  const searchContent = getSearchUiContent(locale);
  return (
    <AttachmentChipShell
      lead={<NoteIcon className="h-3.5 w-3.5" aria-hidden="true" />}
      label={noteChipLabel(item.text) || searchContent.attachNote}
      title={item.text.slice(0, 200)}
      removeLabel={searchContent.removeNote}
      onRemove={onRemove}
    />
  );
}

/** The row of chips. Scrolls sideways, fades at the right edge while more chips sit past it. */
export function AttachmentChipsRail({
  items,
  locale,
  onRemove,
}: {
  items: readonly ComposerAttachment[];
  locale: LocaleDetails;
  onRemove?: (id: string) => void;
}) {
  const railRef = useRef<HTMLDivElement>(null);
  const [more, setMore] = useState(false);
  useEffect(() => {
    const el = railRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const check = () => setMore(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
    check();
    el.addEventListener('scroll', check, { passive: true });
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', check);
      ro.disconnect();
    };
  }, [items.length]);
  if (items.length === 0) return null;
  const fade = more ? 'linear-gradient(to right, black calc(100% - 1.5rem), transparent)' : undefined;
  return (
    <div
      ref={railRef}
      data-attachment-chips=""
      style={fade ? { maskImage: fade, WebkitMaskImage: fade } : undefined}
      className="no-scrollbar flex min-w-0 flex-1 flex-nowrap items-center gap-1.5 overflow-x-auto overscroll-x-contain py-0.5"
    >
      {items.map((item) =>
        item.kind === 'image' ? (
          <ImageAttachmentChip key={item.id} item={item} locale={locale} onRemove={onRemove ? () => onRemove(item.id) : undefined} />
        ) : item.kind === 'link' ? (
          <LinkAttachmentChip key={item.id} item={item} locale={locale} onRemove={onRemove ? () => onRemove(item.id) : undefined} />
        ) : (
          <NoteAttachmentChip key={item.id} item={item} locale={locale} onRemove={onRemove ? () => onRemove(item.id) : undefined} />
        ),
      )}
    </div>
  );
}
